import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Checkbox, InputAdornment, TextField } from '@mui/material';
import {
  Search,
  DeleteRounded,
  GridViewRounded,
  FormatListBulletedRounded,
} from '@mui/icons-material';
import classNames from 'classnames';
import actions from '@src/redux/actions';
import { RECENT_PROJECTS_VIEW } from '@src/constants/educators';
import Tooltip from '@src/components/Tooltip';
import NotificationDialogV3 from '@src/components/NotificationDialogV3';
import Typography from '@src/new-components/Typography';
import { COLOR } from '@src/styles/color';
import ProjectList from './ProjectList';
import ProjectGrid from './ProjectGrid';
import useDeleteProjects from './useDeleteProjects';
import { StyledRecentProjects } from './index.style';

const SEARCH_DEBOUNCE = 400;

const RecentProjects = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // In redux rather than here, so leaving the page and coming back still shows
  // the view that was picked. Only this outlives the page — the keyword and how
  // far the list was read start over, each view fetching its own way.
  const view = useSelector((state) => state.layout.recentProjectsView);
  const [selected, setSelected] = useState([]);
  // The rows the open view is showing, so the bar can count and select them.
  const [pageItems, setPageItems] = useState([]);
  // Handed to the open view after a delete, which drops those rows on the spot.
  const [removedIds, setRemovedIds] = useState([]);

  const hasSelection = selected.length > 0;
  const allSelected = selected.length >= pageItems.length;

  const { target, askOne, askMany, close, confirm, description } =
    useDeleteProjects({
      selected,
      onDone: (ids) => {
        // Only the deleted ones leave, so a single delete keeps the other picks.
        const removed = new Set(ids);
        setSelected((prev) =>
          prev.filter((project) => !removed.has(project.id)),
        );
        setRemovedIds(ids);
      },
    });

  // Debounced here rather than in the hook: a view mounts with the keyword
  // already settled, so switching views does not wait to fetch.
  // utils/debounce shares one timer across callers, so use a local one.
  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedSearch(search.trim()),
      SEARCH_DEBOUNCE,
    );
    return () => clearTimeout(timer);
  }, [search]);

  // Switching views swaps the whole dataset, so nothing stays picked.
  useEffect(() => setSelected((prev) => (prev.length ? [] : prev)), [view]);

  // Rows that left the page cannot stay selected — otherwise a delete would
  // reach projects the user can no longer see.
  useEffect(() => {
    const ids = new Set(pageItems.map((project) => project.id));
    setSelected((prev) => {
      const next = prev.filter((project) => ids.has(project.id));
      return next.length === prev.length ? prev : next;
    });
  }, [pageItems]);

  // Any selection means the checkbox clears it; none means take the page.
  const toggleSelectAll = () => setSelected(hasSelection ? [] : pageItems);

  return (
    <StyledRecentProjects>
      <Typography
        className="recent-title"
        variant="body-20-bold"
        color={COLOR.label2[100]}
      >
        {t('recent')}
      </Typography>

      {/* Picking rows swaps the search and the view buttons for the actions
          that apply to them, the way the project list does it. */}
      <div
        className={classNames('recent-toolbar', {
          'recent-toolbar-selection': hasSelection,
        })}
      >
        {hasSelection ? (
          <>
            <div className="recent-selection">
              <Checkbox
                className="recent-select-all"
                // Without this the ripple takes the theme's primary, which is
                // yellow.
                color="default"
                checked={allSelected}
                indeterminate={!allSelected}
                onChange={toggleSelectAll}
              />
              <Typography variant="body-16-semibold" color={COLOR.label2[100]}>
                {t('deselectProjects', {
                  selected: selected.length,
                  total: pageItems.length,
                })}
              </Typography>
            </div>
            <Button
              className="recent-delete"
              variant="outlined"
              startIcon={
                <DeleteRounded
                  sx={{ fontSize: 20, color: COLOR.secondary[8] }}
                />
              }
              onClick={askMany}
            >
              {t('delete')}
            </Button>
          </>
        ) : (
          <>
            <TextField
              className="recent-search"
              size="small"
              variant="outlined"
              value={search}
              placeholder={t('search')}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: COLOR.indigo[80] }} />
                  </InputAdornment>
                ),
              }}
            />
            <div className="recent-views">
              <Tooltip title={t('gridView')} placement="top" arrow>
                <button
                  type="button"
                  className={classNames('recent-view-button', {
                    'recent-view-button-active':
                      view === RECENT_PROJECTS_VIEW.GRID,
                  })}
                  onClick={() =>
                    dispatch(
                      actions.layout.setRecentProjectsView(
                        RECENT_PROJECTS_VIEW.GRID,
                      ),
                    )
                  }
                >
                  <GridViewRounded />
                </button>
              </Tooltip>
              <Tooltip title={t('listView')} placement="top" arrow>
                <button
                  type="button"
                  className={classNames('recent-view-button', {
                    'recent-view-button-active':
                      view === RECENT_PROJECTS_VIEW.LIST,
                  })}
                  onClick={() =>
                    dispatch(
                      actions.layout.setRecentProjectsView(
                        RECENT_PROJECTS_VIEW.LIST,
                      ),
                    )
                  }
                >
                  <FormatListBulletedRounded />
                </button>
              </Tooltip>
            </div>
          </>
        )}
      </div>

      {/* Each view mounts its own copy of the hook, so switching reloads. */}
      {view === RECENT_PROJECTS_VIEW.LIST ? (
        <ProjectList
          search={debouncedSearch}
          removedIds={removedIds}
          selected={selected}
          onSelect={setSelected}
          onPageItems={setPageItems}
          onDelete={askOne}
        />
      ) : (
        <ProjectGrid
          search={debouncedSearch}
          removedIds={removedIds}
          selected={selected}
          onSelect={setSelected}
          onPageItems={setPageItems}
          onDelete={askOne}
        />
      )}

      {target && (
        <NotificationDialogV3
          open
          name="delete-project"
          widthContent="512px"
          padding="12px 24px"
          paddingHeader="8px 24px"
          title={t('deleteProject')}
          description={description()}
          renderIcon={() => (
            <DeleteRounded sx={{ color: COLOR.textRejected, fontSize: 24 }} />
          )}
          onClose={close}
          actionComponent={
            <div>
              <Button
                className="btn-understood"
                variant="outlined"
                onClick={confirm}
              >
                {t('delete')}
              </Button>
              <Button
                className="btn-understood"
                variant="contained"
                onClick={close}
              >
                {t('cancel')}
              </Button>
            </div>
          }
        />
      )}
    </StyledRecentProjects>
  );
};

export default RecentProjects;
